import type { InitiativeBarDefinition } from '@/initiative';

const REORDER_AND_ROUNDS = {
    spendPreviousOnSelect: true,
    reactivateSpentOnSelect: true,
    skipSpentOnAdvance: true,
    clearSpentOnNewRound: true,
    manualReorder: true,
};

const UNDO_TURN = {
    id: 'toggle-spent',
    label: 'Undo their turn',
    command: 'toggle-spent',
    icon: 'hourglass',
    scope: 'entry',
    when: 'spent',
} as const;

/**
 * Combatants arrive by being dragged onto the bar, which supplies no rolled
 * value, so every turn order here is arranged by hand and `sort` has nothing to
 * sort on. Add a roll input and `order` becomes 'descending' plus a `sort`
 * action — data, not code.
 */
export const turnOrder: InitiativeBarDefinition = {
    id: 'turn-order',
    name: 'Turn order',
    description: 'Everyone acts once per round. Advancing past the last combatant opens the next round.',
    order: 'manual',
    rules: {
        ...REORDER_AND_ROUNDS,
        advanceStartsNewRound: true,
    },
    display: {
        showValue: false,
        valueLabel: '',
        showRound: true,
        roundLabel: 'Round',
    },
    actions: [
        { id: 'advance', label: 'Next turn', command: 'advance', icon: 'advance', scope: 'bar' },
        { id: 'reset', label: 'Back to the top of the round', command: 'reset', icon: 'reset', scope: 'bar' },
        UNDO_TURN,
    ],
};

/** Whoever is acting picks who goes next, so there is nothing to advance to. */
export const popcorn: InitiativeBarDefinition = {
    id: 'popcorn',
    name: 'Popcorn',
    description: 'The combatant acting chooses who goes next. Start a new round once everyone has gone.',
    order: 'manual',
    rules: {
        ...REORDER_AND_ROUNDS,
        advanceStartsNewRound: false,
    },
    display: {
        showValue: false,
        valueLabel: '',
        showRound: false,
        roundLabel: '',
    },
    actions: [{ id: 'reset', label: 'Start a new round', command: 'reset', icon: 'reset', scope: 'bar' }, UNDO_TURN],
};

export const initiativeTypes: InitiativeBarDefinition[] = [turnOrder, popcorn];

export const defaultInitiativeType = turnOrder;

export function initiativeType(id: string | undefined): InitiativeBarDefinition {
    return initiativeTypes.find((type) => type.id === id) ?? defaultInitiativeType;
}
